import React, { useState } from 'react';
import { MessageSquare, LayoutList, Calendar, FolderOpen, Settings, ChevronDown, Search, Star, Plus, Users, ArrowLeftRight, SlidersHorizontal, Hash, Lock, User, Bell, FileText, Image, Video, File, MoreHorizontal, Sparkles, Send, Clock, Paperclip, ThumbsUp, ChevronLeft, ChevronRight, Download, Eye } from 'lucide-react';
import { BOARD_DESIGNS, BoardDesignMainContent, type BoardDesignType } from './BoardDesignDemo';
import { cn } from '../../lib/utils';

/**
 * Navigation Rail + Sidebar 레이아웃 테스트 페이지
 * 
 * 3가지 방안을 비교:
 * A) 콘텐츠 교체형: 상단 고정(프로필+WS) + 하단 콘텐츠만 모듈별 교체
 * B) 헤더 분리형: 모듈별 전용 헤더 + 콘텐츠 (Notion 스타일)
 * C) 탭 전환형: 상단 고정 + 인라인 탭으로 콘텐츠 전환 (Teams 스타일)
 */

type AppModule = 'chat' | 'board' | 'calendar' | 'files';
type LayoutVariant = 'A' | 'B' | 'C';

const MODULES: { module: AppModule; icon: React.ElementType; label: string }[] = [
  { module: 'chat', icon: MessageSquare, label: '채팅' },
  { module: 'board', icon: LayoutList, label: '게시판' },
  { module: 'calendar', icon: Calendar, label: '캘린더' },
  { module: 'files', icon: FolderOpen, label: '파일' },
];

// ─── 공통 Mock 데이터 ────────────────────────────────────────
const MOCK_CHANNELS = [
  { name: '일반', icon: Hash, unread: 3 },
  { name: '공지사항', icon: Hash, unread: 0 },
  { name: '프로젝트-A', icon: Lock, unread: 1 },
  { name: '디자인팀', icon: Hash, unread: 0 },
  { name: '개발-백엔드', icon: Lock, unread: 5 },
];
const MOCK_BOARDS = BOARD_DESIGNS;
const MOCK_CALENDAR_ITEMS = [
  { name: '내 캘린더', color: 'bg-blue-500' },
  { name: '팀 일정', color: 'bg-green-500' },
  { name: '프로젝트 마일스톤', color: 'bg-purple-500' },
  { name: '휴가/부재', color: 'bg-orange-500' },
];
const MOCK_FILES = [
  { name: '문서', icon: FileText, count: 42 },
  { name: '이미지', icon: Image, count: 128 },
  { name: '동영상', icon: Video, count: 15 },
  { name: '기타', icon: File, count: 8 },
];

// ─── Rail 컴포넌트 (공통) ────────────────────────────────────
const MockRail: React.FC<{ active: AppModule; onSelect: (m: AppModule) => void }> = ({ active, onSelect }) => {
  const [appMenuOpen, setAppMenuOpen] = useState(false);
  return (
    <nav className="flex flex-col items-center w-14 shrink-0 h-full py-3 gap-1 relative border-r border-border/40">
      {/* 상단: NextPOD 앱 아이콘 */}
      <div className="relative mb-1">
        <button
          onClick={() => setAppMenuOpen(!appMenuOpen)}
          className="flex items-center justify-center w-11 h-11 rounded-xl bg-primary/15 hover:bg-primary/25 transition-all duration-200 cursor-pointer"
        >
          <img src="/assets/icon/favicon.png" alt="NextPOD" className="size-7 rounded-md" />
        </button>
        {/* 앱 메뉴 팝오버 */}
        {appMenuOpen && (
          <>
            <div className="fixed inset-0 z-40" onClick={() => setAppMenuOpen(false)} />
            <div className="absolute left-full top-0 ml-2 z-50 w-56 rounded-xl border border-border bg-background shadow-lg py-1.5 animate-in fade-in slide-in-from-left-2 duration-150">
              <div className="px-3 py-2 border-b border-border mb-1">
                <p className="text-sm font-bold text-[color:rgb(var(--text-title))]">NextPOD</p>
                <p className="text-meta-sm text-muted-foreground">v1.4.0 · 팀 협업 플랫폼</p>
              </div>
              {[
                { label: '릴리즈 노트', sub: 'v1.4.0' },
                { label: '피드백 보내기' },
              ].map(item => (
                <button key={item.label} onClick={() => setAppMenuOpen(false)} className="w-full flex items-center justify-between px-3 py-2 text-sm text-[color:rgb(var(--text-body))] hover:bg-muted cursor-pointer transition-colors">
                  <span>{item.label}</span>
                  {item.sub && <span className="text-meta-sm text-muted-foreground">{item.sub}</span>}
                </button>
              ))}
              <div className="border-t border-border my-1" />
              <button onClick={() => setAppMenuOpen(false)} className="w-full flex items-center px-3 py-2 text-sm text-[color:rgb(var(--text-body))] hover:bg-muted cursor-pointer transition-colors">이용약관</button>
              <div className="border-t border-border my-1" />
              <button onClick={() => setAppMenuOpen(false)} className="w-full flex items-center px-3 py-2 text-sm text-primary hover:bg-primary/5 cursor-pointer transition-colors">
                <span>앱 설치 (PWA)</span>
              </button>
            </div>
          </>
        )}
      </div>
      <div className="w-7 h-px" style={{ background: 'rgb(var(--rail-separator))' }} />
      {/* 중단: 모듈 아이콘 */}
      <div className="flex flex-col items-center gap-1 flex-1 mt-1">
        {MODULES.map(({ module, icon: Icon, label }) => {
          const isActive = active === module;
          return (
            <button
              key={module}
              onClick={() => onSelect(module)}
              className={cn(
                'flex flex-col items-center justify-center w-11 h-11 rounded-xl transition-all duration-200 cursor-pointer',
                isActive
                  ? 'text-[color:rgb(var(--rail-icon-active))] bg-[color:var(--rail-active-bg)]'
                  : 'text-[color:rgb(var(--rail-icon))] hover:text-foreground hover:bg-accent/50'
              )}
            >
              <Icon className="size-5" strokeWidth={isActive ? 2.2 : 1.8} />
              <span className={cn('text-meta-sm leading-tight mt-0.5 font-medium', isActive ? 'opacity-100' : 'opacity-70')}>
                {label}
              </span>
            </button>
          );
        })}
      </div>
      <div className="w-7 h-px my-1" style={{ background: 'rgb(var(--rail-separator))' }} />
      <button className="flex items-center justify-center w-11 h-11 rounded-xl text-[color:rgb(var(--rail-icon))] hover:text-foreground hover:bg-accent/50 transition-all duration-200 cursor-pointer">
        <Settings className="size-5" strokeWidth={1.8} />
      </button>
    </nav>
  );
};

// ─── 프로필 + WS 스위처 (공통 상단 고정 영역) ─────────────────
const MockProfileAndWS: React.FC = () => (
  <div className="shrink-0 px-4 pt-6 pb-0">
    {/* 프로필 */}
    <div className="mb-6 flex items-center gap-2 rounded-lg">
      <div className="w-12 h-12 rounded-full bg-primary/20 flex items-center justify-center text-primary font-bold text-lg shrink-0">U</div>
      <div className="flex flex-col min-w-0">
        <div className="text-base font-bold text-[color:rgb(var(--text-title))] truncate">사용자 이름</div>
        <div className="text-label font-bold text-muted-foreground truncate">user@example.com</div>
      </div>
    </div>
    {/* Legacy + AI Memo */}
    <div className="mb-3 rounded-control border border-border/60 bg-muted p-1.5">
      <div className="flex items-center gap-1.5 px-1.5 py-1 rounded-lg text-muted-foreground hover:bg-background cursor-pointer transition-colors">
        <Sparkles className="w-4 h-4 text-violet-500 shrink-0" />
        <span className="text-sm truncate">AI Memo</span>
        <span className="text-meta-sm text-muted-foreground ml-auto">beta</span>
      </div>
    </div>
    {/* WS 스위처 */}
    <div className="mb-4">
      <button className="w-full flex items-center gap-2.5 px-3 py-2.5 rounded-xl border border-border/80 bg-muted hover:bg-accent shadow-[0_1px_2px_rgba(0,0,0,0.02)] transition-all cursor-pointer group">
        <div className="w-8 h-8 rounded-lg bg-[rgba(255,127,0,0.1)] text-primary flex items-center justify-center font-bold text-sm shrink-0 border border-[rgba(255,127,0,0.1)]">N</div>
        <div className="flex-1 min-w-0 text-left">
          <p className="text-meta-sm leading-3 text-muted-foreground font-semibold uppercase tracking-wider mb-0.5">Workspace</p>
          <p className="text-sm font-bold text-[color:rgb(var(--text-body))] truncate">NextPOD 팀</p>
        </div>
        <ArrowLeftRight className="w-3 h-3 text-muted-foreground" />
      </button>
    </div>
  </div>
);

// ─── 모듈별 사이드바 콘텐츠 ─────────────────────────────────
const ChatSidebarContent: React.FC = () => (
  <div className="flex-1 overflow-y-auto px-4 pb-4">
    <div className="flex items-center justify-between mb-2">
      <div className="flex items-center gap-1 text-label font-bold text-muted-foreground uppercase">
        <ChevronDown className="h-4 w-4" /> Channels
      </div>
      <div className="flex gap-1">
        <button className="size-6 rounded-md flex items-center justify-center text-muted-foreground hover:text-foreground hover:bg-muted"><MoreHorizontal className="size-4" /></button>
      </div>
    </div>
    <div className="flex items-center gap-1 mb-2">
      <div className="relative flex-1">
        <Search className="absolute left-2 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-muted-foreground" />
        <input className="w-full h-7 pl-7 pr-2 text-xs rounded-md border border-border bg-muted placeholder:text-muted-foreground" placeholder="Search channels..." readOnly />
      </div>
      <button className="size-7 flex items-center justify-center"><Star className="w-4 h-4 text-muted-foreground" /></button>
    </div>
    <div className="space-y-0.5">
      {MOCK_CHANNELS.map(ch => (
        <div key={ch.name} className="flex items-center gap-2 px-2 py-1.5 rounded-lg hover:bg-muted cursor-pointer transition-colors">
          <ch.icon className="w-4 h-4 text-muted-foreground shrink-0" />
          <span className="text-sm text-[color:rgb(var(--text-body))] truncate flex-1">{ch.name}</span>
          {ch.unread > 0 && <span className="text-meta-sm bg-destructive text-white rounded-full px-1.5 min-w-[18px] text-center font-bold">{ch.unread}</span>}
        </div>
      ))}
    </div>
    <div className="border-t border-border my-3" />
    <div className="flex items-center gap-1 text-label font-bold text-muted-foreground uppercase mb-2">
      <ChevronDown className="h-4 w-4" /> Direct Messages
    </div>
    <div className="space-y-0.5">
      {['김철수', '이영희', '박지민'].map(name => (
        <div key={name} className="flex items-center gap-2 px-2 py-1.5 rounded-lg hover:bg-muted cursor-pointer transition-colors">
          <div className="w-5 h-5 rounded-full bg-accent flex items-center justify-center text-meta-sm font-bold text-muted-foreground shrink-0">{name[0]}</div>
          <span className="text-sm text-[color:rgb(var(--text-body))] truncate">{name}</span>
        </div>
      ))}
    </div>
  </div>
);

const BoardSidebarContent: React.FC<{ boardDesign: BoardDesignType; onDesignChange: (d: BoardDesignType) => void }> = ({ boardDesign, onDesignChange }) => (
  <div className="flex-1 overflow-y-auto px-4 pb-4">
    <div className="flex items-center justify-between mb-3">
      <h3 className="text-label font-bold text-muted-foreground uppercase">디자인 패턴</h3>
    </div>
    <div className="space-y-0.5">
      {MOCK_BOARDS.map(b => (
        <div key={b.type} onClick={() => onDesignChange(b.type)}
          className={cn('flex items-center gap-2.5 px-2 py-2 rounded-lg cursor-pointer transition-colors',
            boardDesign === b.type ? 'bg-primary/10 text-primary' : 'hover:bg-muted'
          )}>
          <b.icon className={cn('w-4 h-4 shrink-0', boardDesign === b.type ? 'text-primary' : 'text-primary/70')} />
          <div className="flex-1 min-w-0">
            <span className={cn('text-sm truncate block', boardDesign === b.type ? 'font-semibold text-primary' : 'text-[color:rgb(var(--text-body))]')}>{b.name}</span>
            <span className="text-meta-sm text-muted-foreground">{b.desc}</span>
          </div>
        </div>
      ))}
    </div>
    <div className="border-t border-border my-3" />
    <div className="text-label font-bold text-muted-foreground uppercase mb-2">추천 조합</div>
    <div className="space-y-2 text-meta text-muted-foreground px-2">
      <p>• <strong>general</strong>: Basecamp 카드</p>
      <p>• <strong>notice</strong>: Basecamp + 고정</p>
      <p>• <strong>qna</strong>: Discourse 변형</p>
      <p>• <strong>wiki</strong>: Notion 단순화</p>
      <p>• <strong>task</strong>: Linear 변형</p>
    </div>
  </div>
);

const CalendarSidebarContent: React.FC = () => (
  <div className="flex-1 overflow-y-auto px-4 pb-4">
    <div className="flex items-center justify-between mb-3">
      <h3 className="text-label font-bold text-muted-foreground uppercase">내 캘린더</h3>
      <button className="size-6 rounded-md flex items-center justify-center text-muted-foreground hover:text-foreground hover:bg-muted"><Plus className="size-4" /></button>
    </div>
    <div className="space-y-1">
      {MOCK_CALENDAR_ITEMS.map(cal => (
        <div key={cal.name} className="flex items-center gap-2.5 px-2 py-2 rounded-lg hover:bg-muted cursor-pointer transition-colors">
          <div className={cn('w-3 h-3 rounded-sm shrink-0', cal.color)} />
          <span className="text-sm text-[color:rgb(var(--text-body))] truncate">{cal.name}</span>
        </div>
      ))}
    </div>
    <div className="border-t border-border my-3" />
    <div className="text-label font-bold text-muted-foreground uppercase mb-2">오늘 일정</div>
    <div className="space-y-2">
      {[{ time: '10:00', title: '팀 스탠드업' }, { time: '14:00', title: '디자인 리뷰' }, { time: '16:30', title: '스프린트 회고' }].map(ev => (
        <div key={ev.title} className="flex items-start gap-2 px-2 py-1.5 rounded-lg hover:bg-muted cursor-pointer transition-colors">
          <span className="text-meta-sm text-primary font-mono mt-0.5 shrink-0 w-10">{ev.time}</span>
          <span className="text-sm text-[color:rgb(var(--text-body))] truncate">{ev.title}</span>
        </div>
      ))}
    </div>
  </div>
);

const FilesSidebarContent: React.FC = () => (
  <div className="flex-1 overflow-y-auto px-4 pb-4">
    <div className="flex items-center justify-between mb-3">
      <h3 className="text-label font-bold text-muted-foreground uppercase">파일 분류</h3>
      <button className="size-6 rounded-md flex items-center justify-center text-muted-foreground hover:text-foreground hover:bg-muted"><Plus className="size-4" /></button>
    </div>
    <div className="space-y-0.5">
      {MOCK_FILES.map(f => (
        <div key={f.name} className="flex items-center gap-2.5 px-2 py-2 rounded-lg hover:bg-muted cursor-pointer transition-colors">
          <f.icon className="w-4 h-4 text-primary/70 shrink-0" />
          <span className="text-sm text-[color:rgb(var(--text-body))] truncate flex-1">{f.name}</span>
          <span className="text-meta-sm text-muted-foreground">{f.count}개</span>
        </div>
      ))}
    </div>
    <div className="border-t border-border my-3" />
    <div className="text-label font-bold text-muted-foreground uppercase mb-2">저장 공간</div>
    <div className="px-2">
      <div className="h-2 rounded-full bg-muted overflow-hidden"><div className="h-full bg-primary rounded-full" style={{ width: '35%' }} /></div>
      <p className="text-meta-sm text-muted-foreground mt-1.5">350 MB / 1 GB 사용 중</p>
    </div>
  </div>
);

// MODULE_CONTENT는 board만 특수 처리, 나머지는 그대로
const MODULE_CONTENT_SIMPLE: Record<Exclude<AppModule, 'board'>, React.FC> = { chat: ChatSidebarContent, calendar: CalendarSidebarContent, files: FilesSidebarContent };
const MODULE_LABELS: Record<AppModule, string> = { chat: '채팅', board: '게시판', calendar: '캘린더', files: '파일관리' };

// ─── 모듈별 메인 영역 Mock ──────────────────────────────────
const CHAT_MSGS = [
  { user: '김철수', avatar: 'K', time: '오전 10:23', text: '안녕하세요! 오늘 미팅 자료 공유합니다 📎', hasFile: true },
  { user: '이영희', avatar: 'L', time: '오전 10:25', text: '감사합니다. 확인하겠습니다 👍' },
  { user: '박지민', avatar: 'P', time: '오전 10:31', text: '3페이지 디자인 시안 수정 부탁드려요. 컬러 톤을 좀 더 따뜻하게 변경하면 좋을 것 같습니다.' },
  { user: '김철수', avatar: 'K', time: '오전 10:45', text: '네, 반영해서 오후에 다시 올리겠습니다.' },
  { user: '이영희', avatar: 'L', time: '오전 11:02', text: '스프린트 회고 일정 오후 4시 30분으로 변경되었습니다!' },
];

const ChatMainContent: React.FC = () => (
  <div className="flex flex-col h-full">
    <header className="flex items-center gap-3 px-5 h-14 shrink-0 border-b border-border">
      <Hash className="size-5 text-muted-foreground" /><h2 className="text-base font-semibold">일반</h2>
      <span className="text-xs text-muted-foreground ml-1">5명</span>
      <div className="ml-auto flex gap-1">
        <button className="size-8 rounded-lg flex items-center justify-center hover:bg-muted"><Search className="size-4 text-muted-foreground" /></button>
        <button className="size-8 rounded-lg flex items-center justify-center hover:bg-muted"><Users className="size-4 text-muted-foreground" /></button>
      </div>
    </header>
    <div className="flex-1 overflow-y-auto px-5 py-4 space-y-4">
      {CHAT_MSGS.map((m, i) => (
        <div key={i} className="flex gap-3 group">
          <div className="w-9 h-9 rounded-full bg-primary/15 flex items-center justify-center text-primary font-bold text-sm shrink-0">{m.avatar}</div>
          <div className="min-w-0 flex-1">
            <div className="flex items-baseline gap-2"><span className="text-sm font-semibold text-[color:rgb(var(--text-title))]">{m.user}</span><span className="text-meta-sm text-muted-foreground">{m.time}</span></div>
            <p className="text-sm text-[color:rgb(var(--text-body))] mt-0.5">{m.text}</p>
            {m.hasFile && <div className="mt-1.5 inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg border border-border bg-muted text-xs text-muted-foreground"><Paperclip className="size-3" />미팅자료_v3.pdf</div>}
          </div>
          <div className="opacity-0 group-hover:opacity-100 flex gap-0.5 shrink-0 mt-1 transition-opacity">
            <button className="size-7 rounded-md border border-border bg-background flex items-center justify-center hover:bg-muted"><ThumbsUp className="size-3.5 text-muted-foreground" /></button>
            <button className="size-7 rounded-md border border-border bg-background flex items-center justify-center hover:bg-muted"><MessageSquare className="size-3.5 text-muted-foreground" /></button>
          </div>
        </div>
      ))}
    </div>
    <div className="shrink-0 px-5 py-3 border-t border-border">
      <div className="flex items-center gap-2 rounded-xl border border-border bg-muted px-4 py-2.5">
        <button className="shrink-0"><Paperclip className="size-4 text-muted-foreground" /></button>
        <span className="flex-1 text-sm text-muted-foreground">메시지를 입력하세요...</span>
        <button className="shrink-0 size-8 rounded-lg bg-primary flex items-center justify-center"><Send className="size-4 text-primary-foreground" /></button>
      </div>
    </div>
  </div>
);


// BoardMainContent는 boardDesign prop으로 동적 렌더링
const BoardMainContentWrapper: React.FC<{ design: BoardDesignType }> = ({ design }) => (
  <BoardDesignMainContent design={design} />
);

const DAYS = ['일','월','화','수','목','금','토'];
const CalendarMainContent: React.FC = () => {
  const events = [{ day: 8, title: '팀 스탠드업', color: 'bg-blue-500' }, { day: 12, title: '디자인 리뷰', color: 'bg-green-500' }, { day: 15, title: '스프린트 회고', color: 'bg-purple-500' }, { day: 22, title: '분기 발표', color: 'bg-orange-500' }, { day: 24, title: '오늘', color: 'bg-primary' }];
  return (
    <div className="flex flex-col h-full">
      <header className="flex items-center gap-3 px-5 h-14 shrink-0 border-b border-border">
        <Calendar className="size-5 text-primary" /><h2 className="text-base font-semibold">2026년 4월</h2>
        <div className="flex gap-1 ml-2"><button className="size-7 rounded-md hover:bg-muted flex items-center justify-center"><ChevronLeft className="size-4 text-muted-foreground" /></button><button className="size-7 rounded-md hover:bg-muted flex items-center justify-center"><ChevronRight className="size-4 text-muted-foreground" /></button></div>
        <button className="ml-2 h-7 px-3 rounded-md text-xs font-medium border border-border hover:bg-muted">오늘</button>
        <div className="ml-auto flex gap-1 bg-muted rounded-lg p-0.5"><button className="px-2.5 py-1 rounded-md text-xs font-medium bg-background shadow-sm">월</button><button className="px-2.5 py-1 rounded-md text-xs font-medium text-muted-foreground">주</button><button className="px-2.5 py-1 rounded-md text-xs font-medium text-muted-foreground">일</button></div>
      </header>
      <div className="flex-1 overflow-hidden p-4">
        <div className="grid grid-cols-7 gap-px bg-border rounded-lg overflow-hidden h-full">
          {DAYS.map(d => <div key={d} className="bg-muted/50 text-center py-1.5 text-meta-sm font-semibold text-muted-foreground">{d}</div>)}
          {Array.from({ length: 35 }, (_, i) => {
            const day = i - 2; // 4월 1일이 수요일이라고 가정
            const valid = day >= 1 && day <= 30;
            const ev = events.find(e => e.day === day);
            const isToday = day === 24;
            return (
              <div key={i} className={cn('bg-background p-1.5 min-h-[60px] text-xs', !valid && 'bg-muted/30')}>
                {valid && <span className={cn('inline-flex items-center justify-center size-6 rounded-full text-meta-sm font-medium', isToday ? 'bg-primary text-primary-foreground' : 'text-[color:rgb(var(--text-body))]')}>{day}</span>}
                {ev && !isToday && <div className={cn('mt-0.5 text-meta-sm text-white px-1 py-0.5 rounded truncate', ev.color)}>{ev.title}</div>}
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
};

const FILE_LIST = [
  { name: '프로젝트_제안서_v3.pdf', type: 'PDF', size: '2.4 MB', date: '2시간 전', icon: FileText },
  { name: '디자인_시스템_가이드.fig', type: 'Figma', size: '18 MB', date: '5시간 전', icon: Image },
  { name: '4월_회의록.docx', type: 'DOCX', size: '156 KB', date: '1일 전', icon: FileText },
  { name: '로고_최종.png', type: 'PNG', size: '3.1 MB', date: '2일 전', icon: Image },
  { name: '데모_영상.mp4', type: 'MP4', size: '48 MB', date: '3일 전', icon: Video },
  { name: '서버_구성도.pdf', type: 'PDF', size: '890 KB', date: '5일 전', icon: FileText },
];

const FilesMainContent: React.FC = () => (
  <div className="flex flex-col h-full">
    <header className="flex items-center gap-3 px-5 h-14 shrink-0 border-b border-border">
      <FolderOpen className="size-5 text-primary" /><h2 className="text-base font-semibold">전체 파일</h2>
      <span className="text-xs text-muted-foreground ml-1">193개</span>
      <div className="ml-auto flex gap-2">
        <div className="relative"><Search className="absolute left-2.5 top-1/2 -translate-y-1/2 size-3.5 text-muted-foreground" /><input className="h-8 pl-8 pr-3 text-xs rounded-lg border border-border bg-muted w-48 placeholder:text-muted-foreground" placeholder="파일 검색..." readOnly /></div>
        <button className="h-8 px-3 rounded-lg bg-primary text-primary-foreground text-sm font-medium flex items-center gap-1.5"><Plus className="size-3.5" />업로드</button>
      </div>
    </header>
    <div className="flex-1 overflow-y-auto">
      <table className="w-full">
        <thead><tr className="border-b border-border text-meta-sm font-semibold text-muted-foreground uppercase">
          <th className="text-left px-5 py-2.5 w-[45%]">파일명</th><th className="text-left px-3 py-2.5">유형</th><th className="text-left px-3 py-2.5">크기</th><th className="text-left px-3 py-2.5">업로드</th><th className="text-center px-3 py-2.5">액션</th>
        </tr></thead>
        <tbody>
          {FILE_LIST.map((f, i) => (
            <tr key={i} className="border-b border-border/50 hover:bg-muted/50 cursor-pointer transition-colors">
              <td className="px-5 py-3"><div className="flex items-center gap-2.5"><f.icon className="size-4 text-primary/60 shrink-0" /><span className="text-sm text-[color:rgb(var(--text-body))] truncate">{f.name}</span></div></td>
              <td className="px-3 py-3"><span className="text-meta-sm px-1.5 py-0.5 rounded bg-muted font-medium text-muted-foreground">{f.type}</span></td>
              <td className="px-3 py-3 text-xs text-muted-foreground">{f.size}</td>
              <td className="px-3 py-3 text-xs text-muted-foreground">{f.date}</td>
              <td className="px-3 py-3 text-center"><div className="flex justify-center gap-1"><button className="size-7 rounded-md hover:bg-muted flex items-center justify-center"><Eye className="size-3.5 text-muted-foreground" /></button><button className="size-7 rounded-md hover:bg-muted flex items-center justify-center"><Download className="size-3.5 text-muted-foreground" /></button></div></td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  </div>
);

const MAIN_CONTENT_SIMPLE: Record<Exclude<AppModule, 'board'>, React.FC> = { chat: ChatMainContent, calendar: CalendarMainContent, files: FilesMainContent };

// ─── Layout A: 콘텐츠 교체형 (Slack/Discord 스타일) ──────────
const LayoutA: React.FC<{ active: AppModule; onSelect: (m: AppModule) => void; boardDesign: BoardDesignType; onDesignChange: (d: BoardDesignType) => void }> = ({ active, onSelect, boardDesign, onDesignChange }) => {
  return (
    <div className="flex h-full">
      <MockRail active={active} onSelect={onSelect} />
      <div className="w-[260px] shrink-0 h-full flex flex-col overflow-hidden">
        <MockProfileAndWS />
        {active === 'board'
          ? <BoardSidebarContent boardDesign={boardDesign} onDesignChange={onDesignChange} />
          : React.createElement(MODULE_CONTENT_SIMPLE[active as Exclude<AppModule, 'board'>])}
      </div>
      <div className="flex-1 h-full bg-background rounded-panel ml-2 overflow-hidden">
        {active === 'board'
          ? <BoardMainContentWrapper design={boardDesign} />
          : React.createElement(MAIN_CONTENT_SIMPLE[active as Exclude<AppModule, 'board'>])}
      </div>
    </div>
  );
};

// ─── Layout B: 헤더 분리형 (Notion 스타일) ───────────────────
const LayoutB: React.FC<{ active: AppModule; onSelect: (m: AppModule) => void; boardDesign: BoardDesignType; onDesignChange: (d: BoardDesignType) => void }> = ({ active, onSelect, boardDesign, onDesignChange }) => {
  const Icon = MODULES.find(m => m.module === active)!.icon;
  return (
    <div className="flex h-full">
      <MockRail active={active} onSelect={onSelect} />
      <div className="w-[260px] shrink-0 h-full flex flex-col overflow-hidden">
        <MockProfileAndWS />
        {active !== 'chat' && (
          <div className="px-4 pb-2">
            <div className="flex items-center gap-2 px-2 py-2 rounded-lg bg-primary/5 border border-primary/10">
              <Icon className="size-4 text-primary" />
              <span className="text-sm font-semibold text-primary">{MODULE_LABELS[active]}</span>
            </div>
          </div>
        )}
        {active === 'board'
          ? <BoardSidebarContent boardDesign={boardDesign} onDesignChange={onDesignChange} />
          : React.createElement(MODULE_CONTENT_SIMPLE[active as Exclude<AppModule, 'board'>])}
      </div>
      <div className="flex-1 h-full bg-background rounded-panel ml-2 overflow-hidden">
        {active === 'board'
          ? <BoardMainContentWrapper design={boardDesign} />
          : React.createElement(MAIN_CONTENT_SIMPLE[active as Exclude<AppModule, 'board'>])}
      </div>
    </div>
  );
};

// ─── Layout C: 탭 전환형 (Teams 스타일) ─────────────────────
const LayoutC: React.FC<{ active: AppModule; onSelect: (m: AppModule) => void; boardDesign: BoardDesignType; onDesignChange: (d: BoardDesignType) => void }> = ({ active, onSelect, boardDesign, onDesignChange }) => {
  return (
    <div className="flex h-full">
      <MockRail active={active} onSelect={onSelect} />
      <div className="w-[260px] shrink-0 h-full flex flex-col overflow-hidden">
        <MockProfileAndWS />
        <div className="px-4 mb-2">
          <div className="flex rounded-lg bg-muted p-0.5 gap-0.5">
            {MODULES.map(({ module, icon: Icon, label }) => (
              <button
                key={module}
                onClick={() => onSelect(module)}
                className={cn(
                  'flex-1 flex items-center justify-center gap-1 py-1.5 rounded-md text-meta-sm font-medium transition-all cursor-pointer',
                  active === module
                    ? 'bg-background text-primary shadow-sm'
                    : 'text-muted-foreground hover:text-foreground'
                )}
              >
                <Icon className="size-3.5" />
                {label}
              </button>
            ))}
          </div>
        </div>
        {active === 'board'
          ? <BoardSidebarContent boardDesign={boardDesign} onDesignChange={onDesignChange} />
          : React.createElement(MODULE_CONTENT_SIMPLE[active as Exclude<AppModule, 'board'>])}
      </div>
      <div className="flex-1 h-full bg-background rounded-panel ml-2 overflow-hidden">
        {active === 'board'
          ? <BoardMainContentWrapper design={boardDesign} />
          : React.createElement(MAIN_CONTENT_SIMPLE[active as Exclude<AppModule, 'board'>])}
      </div>
    </div>
  );
};

// ─── 메인 테스트 페이지 ─────────────────────────────────────
const LayoutTestPage: React.FC = () => {
  const [variant, setVariant] = useState<LayoutVariant>('A');
  const [active, setActive] = useState<AppModule>('board');
  const [boardDesign, setBoardDesign] = useState<BoardDesignType>('basecamp');

  const VARIANT_INFO: Record<LayoutVariant, { title: string; desc: string; ref: string }> = {
    A: { title: 'A) 콘텐츠 교체형', desc: 'Rail 클릭 시 사이드바 하단 콘텐츠만 교체. 프로필·WS 고정.', ref: 'Slack, Discord' },
    B: { title: 'B) 헤더 분리형', desc: 'A와 동일 + 비채팅 모듈에 모듈 헤더 배너 추가.', ref: 'Notion, Linear' },
    C: { title: 'C) 탭 전환형', desc: 'WS 스위처 아래에 인라인 탭 바 추가. Rail 없이도 전환 가능.', ref: 'Teams, Figma' },
  };

  return (
    <div className="h-screen w-full flex flex-col bg-muted">
      {/* 상단 컨트롤 바 */}
      <div className="shrink-0 flex items-center gap-4 px-6 py-3 bg-background border-b border-border">
        <h1 className="text-base font-bold text-foreground mr-4">🧪 Navigation Layout Test</h1>
        {(['A', 'B', 'C'] as LayoutVariant[]).map(v => (
          <button
            key={v}
            onClick={() => setVariant(v)}
            className={cn(
              'px-4 py-2 rounded-lg text-sm font-medium transition-all cursor-pointer border',
              variant === v
                ? 'bg-primary text-primary-foreground border-primary'
                : 'bg-background text-foreground border-border hover:bg-muted'
            )}
          >
            {VARIANT_INFO[v].title}
          </button>
        ))}
        <div className="ml-auto text-xs text-muted-foreground max-w-[300px]">
          <p className="font-semibold">{VARIANT_INFO[variant].desc}</p>
          <p className="mt-0.5">참고: {VARIANT_INFO[variant].ref}</p>
        </div>
      </div>
      {/* 레이아웃 영역 */}
      <div className="flex-1 min-h-0 p-2 gap-2">
        {variant === 'A' && <LayoutA active={active} onSelect={setActive} boardDesign={boardDesign} onDesignChange={setBoardDesign} />}
        {variant === 'B' && <LayoutB active={active} onSelect={setActive} boardDesign={boardDesign} onDesignChange={setBoardDesign} />}
        {variant === 'C' && <LayoutC active={active} onSelect={setActive} boardDesign={boardDesign} onDesignChange={setBoardDesign} />}
      </div>
    </div>
  );
};

export default LayoutTestPage;
